import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const orgchartCompensationFieldPack: FieldPack = {
  id: 'orgchart-compensation',
  name: 'Cost & Compensation',
  description: 'Salary and pay band data for cost modeling during workforce planning and M&A equivalency',
  version: '1.0.0',
  dependencies: ['orgchart-base'],
  fields: [
    {
      id: 'salary',
      label: 'Base Salary',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 90,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'currency',
      label: 'Currency',
      // ISO 4217 is a real standard, so the vocabulary can ship — unlike the
      // per-person amounts in this pack, a currency list discloses nothing.
      // The common trading set is offered directly; custom entry covers the
      // long tail rather than shipping all ~180 codes.
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['compensation'],
      priority: 85,
      namespace: 'comp',
      maxLength: 3,
      options: [
        { value: 'USD', label: 'USD — US Dollar' },
        { value: 'EUR', label: 'EUR — Euro' },
        { value: 'GBP', label: 'GBP — British Pound' },
        { value: 'AUD', label: 'AUD — Australian Dollar' },
        { value: 'CAD', label: 'CAD — Canadian Dollar' },
        { value: 'NZD', label: 'NZD — New Zealand Dollar' },
        { value: 'CHF', label: 'CHF — Swiss Franc' },
        { value: 'JPY', label: 'JPY — Japanese Yen' },
        { value: 'CNY', label: 'CNY — Chinese Yuan' },
        { value: 'INR', label: 'INR — Indian Rupee' },
        { value: 'SGD', label: 'SGD — Singapore Dollar' },
        { value: 'HKD', label: 'HKD — Hong Kong Dollar' },
        { value: 'AED', label: 'AED — UAE Dirham' },
        { value: 'ZAR', label: 'ZAR — South African Rand' },
        { value: 'BRL', label: 'BRL — Brazilian Real' },
        { value: 'MXN', label: 'MXN — Mexican Peso' },
        { value: 'SEK', label: 'SEK — Swedish Krona' },
        { value: 'NOK', label: 'NOK — Norwegian Krone' },
        { value: 'DKK', label: 'DKK — Danish Krone' },
        { value: 'PLN', label: 'PLN — Polish Zloty' },
        { value: 'LKR', label: 'LKR — Sri Lankan Rupee' },
      ],
      sensitivity: 'hr_confidential',
    },
    {
      id: 'payFrequency',
      label: 'Pay Frequency',
      type: FieldType.SELECT,
      tags: ['compensation'],
      priority: 80,
      namespace: 'comp',
      options: [
        { value: 'annual', label: 'Annual' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'biweekly', label: 'Bi-Weekly' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'hourly', label: 'Hourly' },
      ],
      sensitivity: 'hr_confidential',
    },
    {
      id: 'band',
      label: 'Pay Band',
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['compensation'],
      priority: 75,
      namespace: 'comp',
      sensitivity: 'hr_confidential',
    },
    {
      id: 'payRangeMin',
      label: 'Pay Range Minimum',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 70,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'payRangeMid',
      label: 'Pay Range Midpoint',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 65,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'payRangeMax',
      label: 'Pay Range Maximum',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 60,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'compaRatio',
      label: 'Compa-Ratio',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 55,
      namespace: 'comp',
      min: 0,
      max: 2,
      step: 0.01,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'bonusTarget',
      label: 'Bonus Target',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 50,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'totalComp',
      label: 'Total Compensation',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 45,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'annualCost',
      label: 'Annual Employment Cost',
      type: FieldType.NUMBER,
      tags: ['compensation'],
      priority: 40,
      namespace: 'comp',
      min: 0,
      sensitivity: 'hr_confidential',
    },
    {
      id: 'lastCompChangeDate',
      label: 'Last Comp Change',
      type: FieldType.DATE,
      tags: ['compensation'],
      priority: 35,
      namespace: 'comp',
      sensitivity: 'hr_confidential',
    },
  ],
};
